<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import type { VpnKey } from '@/types'
import ServerHealthIndicators from '@/components/health/ServerHealthIndicators.vue'
import SublinkIncludeSwitch from '@/components/keys/SublinkIncludeSwitch.vue'
import { useUserStore } from '@/stores/user'
import { isKeyExcludedBySubscriptionFilters, keyNumberLabel, protocolCoreLabel, protocolLabel } from '@/utils/keys'
import { destroyPopover, showPopover } from '@/utils/popovers'

const props = defineProps<{ keyItem: VpnKey }>()
defineEmits<{ showKey: [VpnKey]; showQr: [VpnKey] }>()
const user = useUserStore()

const coreBtn = ref<HTMLElement | null>(null)
const coreLabel = computed(() => protocolCoreLabel(props.keyItem))

function openCorePopover(): void {
  if (!coreBtn.value || !coreLabel.value) return
  showPopover(coreBtn.value, `Ядро "${coreLabel.value}"`, 'right', 'info')
}

onBeforeUnmount(() => {
  if (coreBtn.value) destroyPopover(coreBtn.value)
})
</script>

<template>
  <div class="flex flex-wrap items-center justify-between gap-2 border-b border-white-light/60 py-2 last:border-0">
    <div class="flex flex-wrap items-center gap-2 text-xs">
      <strong>{{ protocolLabel(keyItem) }}</strong>
      <template v-if="keyItem.is_expired">
        <span class="text-muted">|</span>
        <span class="badge badge-danger">Истек</span>
      </template>
      <template v-else-if="keyItem.display_server_id">
        <span class="text-muted">|</span>
        <ServerHealthIndicators
          :server-id="keyItem.display_server_id"
          :transit-server-id="keyItem.transit_server_id"
          compact
        />
        <template v-if="coreLabel">
          <span class="text-muted">|</span>
          <button
            ref="coreBtn"
            type="button"
            class="badge badge-info"
            data-theme="info"
            @click="openCorePopover"
          >
            {{ coreLabel }}
          </button>
        </template>
      </template>
    </div>
    <div class="flex items-center gap-2 self-center">
      <div class="inline-flex items-center gap-1.5 shrink-0">
        <span
          v-if="keyNumberLabel(keyItem)"
          class="inline-flex items-center text-xs font-bold leading-none text-black whitespace-nowrap dark:text-white"
        >
          {{ keyNumberLabel(keyItem) }}
        </span>
        <SublinkIncludeSwitch
          :key-id="keyItem.id"
          :model-value="keyItem.include_in_sublink !== false"
          :blocked-by-filter="isKeyExcludedBySubscriptionFilters(keyItem, user.subscriptionFilters)"
        />
      </div>
      <button
        type="button"
        class="btn btn-sm btn-outline-primary"
        :disabled="!keyItem.config_string"
        @click="$emit('showKey', keyItem)"
      >
        Ключ
      </button>
      <button
        type="button"
        class="btn btn-sm btn-outline-primary"
        :disabled="!(keyItem.qr_data || keyItem.config_string)"
        @click="$emit('showQr', keyItem)"
      >
        QR
      </button>
    </div>
  </div>
</template>
